// src/pages/shipment-details/ShipmentDetails.jsx
// @ts-nocheck
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { parseISO, format } from "date-fns";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getOrderStatusName, OrderBusinessStatusEnum } from "../utils/enums";
import { formatInIST } from "../utils/dateHelpers";
import ClaimUploadModal from "../components/ClaimUploadModal";
import CreateTicketModal from "../components/support/CreateTicketModal";
import ticketService from "../services/ticketService";
import useAuth from "../hooks/useAuth";
import OrderWeightDisputeCard from "./weight-dispute/OrderWeightDisputeCard";
import { normalizeWeightDisputes } from "./weight-dispute/weightDisputeApi";
import { useToast } from "../components/toast/ToastProvider";
import { motion, useReducedMotion } from "framer-motion";
import {
  MapPin,
  Home,
  RotateCcw,
  Package as PackageIcon,
  Radio,
  Tag,
  Camera,
  FileCheck,
  Layers,
  XCircle,
  ShieldAlert,
  Video,
  Info,
  MessageSquare,
  RefreshCw,
  Phone,
  Mail,
  MoveRight,
  CreditCard,
  Wallet as WalletIcon,
  CalendarDays,
  Truck,
  ExternalLink,
  Clock,
  AlertTriangle,
  Scale as ScaleIcon,
  Copy as CopyIcon,
  Check as CheckIcon,
} from "lucide-react";
import {
  downloadRemoteFile,
  DownloadBlockedError,
  safeFilenameSegment,
} from "../utils/download";

/* ----------------------------- Helpers ----------------------------- */
// Status codes that need support ticket button
const STATUSES_REQUIRING_SUPPORT = [
  OrderBusinessStatusEnum.PICKUP_FAILED.code,
  OrderBusinessStatusEnum.FAILED_DELIVERY.code,
  OrderBusinessStatusEnum.RTO.code,
  OrderBusinessStatusEnum.RTO_FAILED.code,
  OrderBusinessStatusEnum.DAMAGED.code,
  OrderBusinessStatusEnum.SHIPMENT_DELAYED.code,
  OrderBusinessStatusEnum.LOST.code,
  OrderBusinessStatusEnum.CONTACT_CUSTOMER_CARE.code,
  OrderBusinessStatusEnum.SHIPMENT_HELD.code,
  OrderBusinessStatusEnum.RTO_INTRANSIT.code,
  OrderBusinessStatusEnum.RTO_SHIPMENT_DELAY.code,
  OrderBusinessStatusEnum.ORDER_CANCELLATION_PENDING.code,
  OrderBusinessStatusEnum.PICKUP_CANCELLED_BY_CARRIER.code,
  OrderBusinessStatusEnum.UNKNOWN.code,
];

const PICKUP_PENDING_CODES = [
  OrderBusinessStatusEnum.PICKUP_PENDING.code,
  OrderBusinessStatusEnum.OUT_FOR_PICKUP.code,
  OrderBusinessStatusEnum.PICKUP_FAILED.code,
  OrderBusinessStatusEnum.ORDER_PLACED.code,
  OrderBusinessStatusEnum.AWB_REGISTERED.code,
];

/* The forward journey as milestones; a stage is done when ANY history code belongs to it. */
const JOURNEY_STAGES = [
  { label: "Placed", codes: [OrderBusinessStatusEnum.ORDER_PLACED.code, OrderBusinessStatusEnum.AWB_REGISTERED.code, OrderBusinessStatusEnum.MANIFESTED.code] },
  { label: "Pickup", codes: [OrderBusinessStatusEnum.PICKUP_PENDING.code, OrderBusinessStatusEnum.OUT_FOR_PICKUP.code] },
  { label: "Picked up", codes: [OrderBusinessStatusEnum.PICKED_UP.code] },
  { label: "In transit", codes: [OrderBusinessStatusEnum.INTRANSIT.code, OrderBusinessStatusEnum.ORIGIN_CITY_IN.code, OrderBusinessStatusEnum.ORIGIN_CITY_OUT.code, OrderBusinessStatusEnum.DESTINATION_HUB_IN.code] },
  { label: "Out for delivery", codes: [OrderBusinessStatusEnum.OUT_FOR_DELIVERY.code] },
  { label: "Delivered", codes: [OrderBusinessStatusEnum.DELIVERED.code] },
];

/* Once RTO begins the forward journey is over — the rail retells the story as a return. */
const RTO_STAGES = [
  { label: "Placed", codes: [OrderBusinessStatusEnum.ORDER_PLACED.code, OrderBusinessStatusEnum.AWB_REGISTERED.code, OrderBusinessStatusEnum.MANIFESTED.code] },
  { label: "Picked up", codes: [OrderBusinessStatusEnum.PICKED_UP.code] },
  { label: "RTO initiated", codes: [OrderBusinessStatusEnum.RTO_REQUESTED.code, OrderBusinessStatusEnum.RTO.code] },
  { label: "RTO in transit", codes: [OrderBusinessStatusEnum.RTO_INTRANSIT.code, OrderBusinessStatusEnum.RTO_SHIPMENT_DELAY.code] },
  { label: "RTO out for delivery", codes: [OrderBusinessStatusEnum.RTO_OUT_FOR_DELIVERY.code] },
  { label: "Returned to you", codes: [OrderBusinessStatusEnum.RTO_DELIVERED.code] },
];
const RTO_CODES = [
  OrderBusinessStatusEnum.RTO_REQUESTED.code, OrderBusinessStatusEnum.RTO.code,
  OrderBusinessStatusEnum.RTO_OUT_FOR_DELIVERY.code, OrderBusinessStatusEnum.RTO_DELIVERED.code,
  OrderBusinessStatusEnum.RTO_FAILED.code, OrderBusinessStatusEnum.RTO_INTRANSIT.code,
  OrderBusinessStatusEnum.RTO_CONTACT_CUSTOMER_CARE.code, OrderBusinessStatusEnum.RTO_SHIPMENT_DELAY.code,
];
const CANCEL_FAMILY = [
  OrderBusinessStatusEnum.CANCELLED_ORDER.code,
  OrderBusinessStatusEnum.PICKUP_CANCELLED_BY_CARRIER.code,
  OrderBusinessStatusEnum.PICKUP_CANCELLED_BY_SHIPPER.code,
  OrderBusinessStatusEnum.ORDER_CANCELLED_BY_END_CUSTOMER_DURING_CONFIRMATION.code,
];

/* Latest history timestamp for any of the given codes. */
const latestTsFor = (statuses, codes) => {
  const codeSet = new Set(codes.map(String));
  let best = null, bestMs = -Infinity;
  (statuses || []).forEach((st) => {
    if (!codeSet.has(String(st.order_status_code || ""))) return;
    const ms = Math.max(safeMs(st.order_status_time), safeMs(st.created_date_time));
    if (ms > bestMs) { bestMs = ms; best = st.order_status_time || st.created_date_time; }
  });
  return best;
};

/* Carrier artifacts exist only once the shipment enters the carrier network; EPOD only after a delivery. */
const PRE_NETWORK_CODES = [
  ...PICKUP_PENDING_CODES,
  OrderBusinessStatusEnum.MANIFESTED.code,
  OrderBusinessStatusEnum.CANCELLED_ORDER.code,
  OrderBusinessStatusEnum.ORDER_CANCELLATION_PENDING.code,
  OrderBusinessStatusEnum.PICKUP_CANCELLED_BY_CARRIER.code,
  OrderBusinessStatusEnum.PICKUP_CANCELLED_BY_SHIPPER.code,
  OrderBusinessStatusEnum.ORDER_CONFIRMATION_AWAITED.code,
  OrderBusinessStatusEnum.ORDER_CONFIRMATION_FAILED.code,
  OrderBusinessStatusEnum.ORDER_CONFIRMATION_RECEIVED_FOR_SHIPMENT_PROCESSING.code,
  OrderBusinessStatusEnum.ORDER_CANCELLED_BY_END_CUSTOMER_DURING_CONFIRMATION.code,
  OrderBusinessStatusEnum.UNKNOWN.code,
];
const DELIVERED_CODES = [
  OrderBusinessStatusEnum.DELIVERED.code,
  OrderBusinessStatusEnum.RTO_DELIVERED.code,
  OrderBusinessStatusEnum.EXCHANGE_DELIVERED.code,
];
const inCarrierNetwork = (code) => Boolean(code) && !PRE_NETWORK_CODES.includes(String(code));
const isDeliveredState = (code) => DELIVERED_CODES.includes(String(code ?? ""));

/* Cancellation is meaningless once the shipment is delivered, cancelled, or mid-cancellation. */
const NON_CANCELLABLE_CODES = [
  OrderBusinessStatusEnum.DELIVERED.code,
  OrderBusinessStatusEnum.RTO_DELIVERED.code,
  OrderBusinessStatusEnum.CANCELLED_ORDER.code,
  OrderBusinessStatusEnum.ORDER_CANCELLATION_PENDING.code,
  OrderBusinessStatusEnum.PICKUP_CANCELLED_BY_CARRIER.code,
  OrderBusinessStatusEnum.PICKUP_CANCELLED_BY_SHIPPER.code,
];
const isCancellable = (code) => !NON_CANCELLABLE_CODES.includes(String(code ?? ""));

function statusPillTone(code) {
  const c = String(code ?? "");
  if (c === "100") return "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200";
  if (["110", "120", "170"].includes(c)) return "bg-rose-100 text-rose-700 ring-1 ring-rose-200";
  return "bg-sky-100 text-sky-700 ring-1 ring-sky-200";
}
const safeMs = (iso) => {
  if (!iso) return -Infinity;
  try {
    const d = parseISO(iso);
    return Number.isNaN(d.getTime()) ? -Infinity : d.getTime();
  } catch {
    return -Infinity;
  }
};
const prettyMoney = (n) => `₹${Number(n ?? 0).toFixed(2)}`;
// Same lenient parsing + IST rendering as formatInIST, just without seconds.
const fmtDayTime = (iso) => {
  if (!iso) return "N/A";
  const date = new Date(iso);
  if (isNaN(date)) return "N/A";
  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};
const gramsToKg = (g) => (g || g === 0 ? `${(Number(g) / 1000).toFixed(2)} kg` : "N/A");

/** Latest created_date_time from History API business codes (for “Last updated” + Order Date tile) */
const getLatestHistoryUpdate = (statuses = []) => {
  if (!Array.isArray(statuses) || statuses.length === 0) return null;
  const latest = statuses.reduce((best, cur) => {
    const bestMs = safeMs(best?.created_date_time);
    const curMs = safeMs(cur?.created_date_time);
    return curMs >= bestMs ? cur : best;
  });
  return latest?.created_date_time || null;
};

/* ================================ Page ================================ */
export default function ShipmentDetails() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { orderRef } = useParams();
  const [sp] = useSearchParams();
  const fromPage = Number(sp.get("page") || state?.fromPage || 1);

  const { showToast } = useToast();
  const [shipment, setShipment] = useState(state?.shipment || null);
  const [businessStatuses, setBusinessStatuses] = useState([]);
  const [weightDisputes, setWeightDisputes] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [labelError, setLabelError] = useState("");

  // Claim modal controls
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [claimModalType, setClaimModalType] = useState("damage"); // "damage" | "weight_dispute"

  const openDamageClaim = () => {
    setClaimModalType("damage");
    setClaimModalOpen(true);
  };

  const openWeightDispute = () => {
    setClaimModalType("weight_dispute");
    setClaimModalOpen(true);
  };

  // Support ticket modal controls
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [supportOrderContext, setSupportOrderContext] = useState(null);
  const { getMerchantId } = useAuth();

  const handleOpenSupport = async () => {
    const merchantId = getMerchantId();
    if (!merchantId) {
      alert("Please log in to create a support ticket");
      return;
    }

    const sh = shipment;
    const orderRef = sh?.order_reference_number;
    if (!orderRef) {
      alert("Order reference number not found");
      return;
    }

    try {
      // Try to get existing ticket
      const existingTicket = await ticketService.getTicketByOrder(merchantId, orderRef);
      // If ticket exists, navigate to it
      navigate(`/support/tickets/${existingTicket.public_id}`);
    } catch (err) {
      // If 404, ticket doesn't exist - open create modal
      if (err?.response?.status === 404) {
        setSupportOrderContext({
          orderReferenceNumber: orderRef,
          awbNumber: sh?.awb_number,
          customerName: sh?.drop_name,
          customerPhone: sh?.drop_phone,
          customerEmail: sh?.drop_email,
        });
        setSupportModalOpen(true);
      } else {
        alert(err?.response?.data?.message || "Failed to check for existing ticket");
      }
    }
  };

  const printRootRef = useRef(null);

  /* Latest code + friendly name */
  const latestBusinessCode = useMemo(() => {
    if (!businessStatuses.length) return null;
    const latest = businessStatuses.reduce((best, cur) => {
      const bestMs = Math.max(safeMs(best.order_status_time), safeMs(best.created_date_time));
      const curMs = Math.max(safeMs(cur.order_status_time), safeMs(cur.created_date_time));
      return curMs >= bestMs ? cur : best;
    });
    return latest?.order_status_code ?? null;
  }, [businessStatuses]);

  const currentStatusName = useMemo(() => {
    if (latestBusinessCode) return getOrderStatusName(String(latestBusinessCode));
    if (shipment?.latest_order_status_code) return getOrderStatusName(String(shipment.latest_order_status_code));
    return shipment?.latest_order_status?.replace(/_/g, " ") || "N/A";
  }, [latestBusinessCode, shipment]);

  /* Merchant settlement type (prepaid / postpaid / COD netoff) — drives the Charges card copy. */
  const [walletType, setWalletType] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const merchantId = localStorage.getItem("merchantId");
    if (!token || !merchantId) return;
    axios
      .get(`${process.env.REACT_APP_OMS_BASE_URL}/v1/wallet/${merchantId}/balance`, { headers: { Authorization: token } })
      .then((res) => setWalletType(String(res.data?.type || "").toUpperCase()))
      .catch(() => setWalletType(null));
  }, []);

  /* Fetch by orderRef if needed */
  useEffect(() => {
    (async () => {
      if (!shipment && orderRef) {
        try {
          setIsFetching(true);
          const merchantId = localStorage.getItem("merchantId");
          const authToken = localStorage.getItem("authToken");
          if (!merchantId || !authToken) return;
          const res = await axios.post(
            `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_SEARCH_ORDERS}`,
            { merchant_id: merchantId, order_reference_number: orderRef, limit: 1, skip: 0 },
            { headers: { Authorization: authToken, "x-merchant-id": merchantId, "Content-Type": "application/json" } }
          );
          setShipment(res.data?.orders?.[0] || null);
        } catch (e) {
          console.error("Failed to fetch shipment:", e);
        } finally {
          setIsFetching(false);
        }
      }
    })();
  }, [orderRef, shipment]);

  /* Fetch history */
  const fetchOrderHistory = useCallback(async (orderReferenceNumber, signal) => {
    try {
      const merchantId = localStorage.getItem("merchantId");
      const authToken = localStorage.getItem("authToken");
      if (!merchantId || !authToken || signal?.aborted) return;

      const apiUrl = `${process.env.REACT_APP_OMS_BASE_URL}/v2/oms/order/tracking/history`;
      const response = await axios.post(
        apiUrl,
        { order_reference_number: orderReferenceNumber },
        { headers: { "x-merchant-id": merchantId, Authorization: authToken, "Content-Type": "application/json" }, signal }
      );

      const statuses = (response.data.order_processing_business_status_codes || [])
        .slice()
        .sort((a, b) => safeMs(a.order_status_time || a.created_date_time) - safeMs(b.order_status_time || b.created_date_time));
      setBusinessStatuses(statuses);
      setWeightDisputes(normalizeWeightDisputes(response.data.weight_disputes));
    } catch (error) {
      console.error("Error fetching order history:", error);
    }
  }, []);

  useEffect(() => {
    const ref = shipment?.order_reference_number || orderRef;
    if (!ref) return;
    const controller = new AbortController();
    fetchOrderHistory(ref, controller.signal);
    return () => controller.abort();
  }, [shipment?.order_reference_number, orderRef, fetchOrderHistory]);

  /* Actions */
  const handleCancel = async () => {
    if (!shipment?.awb_number) return;
    const confirmCancel = window.confirm(`Are you sure you want to cancel AWB ${shipment.awb_number}?`);
    if (!confirmCancel) return;
    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) return;
      await axios.post(
        `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_CANCEL_ORDER_URL}`,
        { waybill: shipment.awb_number },
        { headers: { Authorization: authToken, "Content-Type": "application/json" } }
      );
      navigate("/all-shipments", { state: { page: fromPage } });
    } catch (err) {
      console.error("Cancel failed:", err);
    }
  };

  const refetchLabel = async () => {
    try {
      setLabelError("");
      const merchantId = localStorage.getItem("merchantId");
      const authToken = localStorage.getItem("authToken");
      if (!merchantId || !authToken || !shipment?.order_reference_number) return;
      const apiUrl = `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_GENERATE_REFRESH_LABEL}`;
      const payload = { merchant_id: merchantId, order_reference_number: shipment.order_reference_number };
      const response = await axios.put(apiUrl, payload, {
        headers: { Authorization: authToken, "Content-Type": "application/json" },
      });
      const labelUrl = response.data.shipment_label_presigned_url;
      if (labelUrl) {
        const a = document.createElement("a");
        a.href = labelUrl;
        a.download = `${shipment.order_reference_number}-shipment-label.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        setLabelError("Label URL not available. Please try again.");
      }
    } catch (error) {
      console.error("Error refetching label:", error);
      setLabelError("Label regeneration failed. Please try again.");
    }
  };

  /* ---------------------- Export PDF (stacked timeline) ---------------------- */
  const handleExportPDF = useCallback(async () => {
    if (!printRootRef.current) return;
    const html2pdf = (await import("html2pdf.js")).default;

    // Show the hidden print layout just for capture
    const node = printRootRef.current;
    const old = node.style.display;
    node.style.display = "block";

    const filename = `${shipment?.order_reference_number || orderRef || "shipment"}-details.pdf`;

    try {
      await html2pdf()
        .set({
          filename,
          margin: [8, 8, 10, 8], // mm
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, scrollY: 0 },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["css", "legacy"], avoid: ".pdf-avoid-break" },
        })
        .from(node)
        .save();
    } catch (e) {
      console.error("PDF export failed:", e);
    } finally {
      node.style.display = old || "none";
    }
  }, [orderRef, shipment]);

  /* ---------------------- Carrier image download ---------------------- */
  const [carrierImageLoading, setCarrierImageLoading] = useState(null);

  const handleDownloadCarrierImage = async (slug, friendlyLabel) => {
    const awb = shipment?.awb_number;
    if (!awb) {
      showToast("AWB number not available for this shipment", "error");
      return;
    }
    if (carrierImageLoading) return;
    const authToken = localStorage.getItem("authToken");
    const merchantId = localStorage.getItem("merchantId");
    if (!authToken || !merchantId) {
      showToast("Please log in to download carrier images", "error");
      return;
    }
    try {
      setCarrierImageLoading(slug);
      const apiUrl = `${process.env.REACT_APP_OMS_BASE_URL}/v2/oms/carrier-images/${slug}?awbNumber=${encodeURIComponent(awb)}`;
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: authToken,
          "x-merchant-id": merchantId,
          "Content-Type": "application/json",
        },
      });
      const data = response?.data || {};
      const presigned =
        data.presignedUrlForImageAccess || data.presigned_url_for_image_access;
      const suggestedName =
        data.suggestedDownloadFileName || data.suggested_download_file_name;
      if (!presigned) {
        showToast(`${friendlyLabel} not yet received from carrier`, "error");
        return;
      }
      const ext =
        (data.fileExtension || data.file_extension || "jpg")
          .toString()
          .replace(/^\./, "");
      const fallbackName =
        safeFilenameSegment(suggestedName) || `${awb}-${slug}.${ext}`;
      await downloadRemoteFile(presigned, fallbackName);
      showToast(`${friendlyLabel} downloaded`, "success");
    } catch (err) {
      if (err && err.name === "DownloadBlockedError") {
        showToast(
          `${friendlyLabel} could not be downloaded directly. Please contact support.`,
          "error",
        );
        return;
      }
      const status = err?.response?.status;
      const code =
        err?.response?.data?.code || err?.response?.data?.errorCode;
      const message =
        err?.response?.data?.message || err?.response?.data?.errorMessage;
      if (status === 404 || code === "CARRIER_IMAGE_NOT_FOUND") {
        showToast(`${friendlyLabel} not yet received for this AWB`, "error");
      } else {
        showToast(message || `Failed to download ${friendlyLabel}`, "error");
      }
    } finally {
      setCarrierImageLoading(null);
    }
  };

  /* Render gates */
  if (isFetching) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-300 border-t-slate-500" />
        <p className="mt-4 text-gray-600 font-medium">Loading shipment details…</p>
      </div>
    );
  }
  if (!shipment) return <div className="p-6 text-gray-700">No shipment found for reference: {orderRef}</div>;

  const sh = shipment;
  const packages = Array.isArray(sh.shipment_packages) ? sh.shipment_packages : [];
  const flatItems = packages.flatMap((pkg) => (pkg.items || []).map((item) => ({ ...item, _pkg: pkg })));
  const itemsTotal = flatItems.reduce((sum, it) => sum + Number(it.quantity ?? 0) * Number(it.item_payment_details?.unit_price ?? 0), 0);

  const latestHistoryISO = getLatestHistoryUpdate(businessStatuses);
  const latestHistoryPretty = latestHistoryISO ? formatInIST(latestHistoryISO) : "N/A";

  const addressLine = (addr, city, dist, state, pin) => [addr, city, dist, state, pin].filter(Boolean).join(", ");

  return (
    <>
      {/* LIVE PAGE (unchanged UI) */}
      <div className="container max-w-screen-2xl mx-auto px-4 lg:px-6 py-10 space-y-8">
        {/* Identity Bar */}
        <Reveal order={0}>
        <IdentityBar
          sh={sh}
          latestBusinessCode={latestBusinessCode}
          currentStatusName={currentStatusName}
          onRefetchLabel={refetchLabel}
          onCancel={handleCancel}
          onExportPDF={handleExportPDF}
          labelError={labelError}
          openDamageClaim={openDamageClaim}
          openWeightDispute={openWeightDispute}
          claimModalOpen={claimModalOpen}
          setClaimModalOpen={setClaimModalOpen}
          shipment={shipment}
          claimModalType={claimModalType}
          onSupportClick={handleOpenSupport}
          onDownloadCarrierImage={handleDownloadCarrierImage}
          carrierImageLoading={carrierImageLoading}
          businessStatuses={businessStatuses}
        />
        </Reveal>

        {/* Money + weight + activity */}
        <Reveal order={1}>
        <section className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <ChargesCard sh={sh} walletType={walletType} />
          <KpiWeightsCompact
            deadGrams={sh.dead_weight_in_grams}
            volGrams={sh.volumetric_weight_in_grams}
            chargedCategory={sh.charged_weight_category}
            dims={sh.shipment_package_dimensions}
          />
          <MetricTile
            icon={<CalendarDays className="h-4 w-4" />}
            tint="bg-amber-50 text-amber-600"
            label="Last Activity"
            value={latestHistoryISO ? fmtDayTime(latestHistoryISO) : fmtDayTime(sh.order_placed_date_time)}
            compactValue
            meta={
              <div className="flex items-baseline justify-between gap-2">
                <span>Placed</span>
                <span className="font-semibold tabular-nums text-slate-700">{fmtDayTime(sh.order_placed_date_time)}</span>
              </div>
            }
          />
        </section>
        </Reveal>

        {/* Metadata */}
        <Reveal order={2}>
        <MetadataGrid
          title="Shipment Metadata"
          items={[
            { k: "Order Type", v: sh.order_type },
            { k: "Pickup Org", v: sh.pickup_organisation },
            { k: "Shipment Type", v: sh.shipment_order_type },
            { k: "Channel", v: sh.channel_name || sh.marketplace_channel_name || "Direct" },
            { k: "Declared Value", v: prettyMoney(sh.payment_details?.total_amount), mono: true },
            sh.marketplace_channel_order_number
              ? { k: "Channel Order #", v: sh.marketplace_channel_order_number, mono: true }
              : null,
            { k: "Invoice #", v: sh.invoice_number, mono: true },
          ]}
        />

        </Reveal>

        {/* Addresses */}
        <Reveal order={3}>
        <ResponsiveAddresses
          pickup={{
            name: sh.pickup_name,
            phone: sh.pickup_poc_contact_number,
            email: sh.pickup_poc_email,
            addr: addressLine(sh.pickup_address, sh.pickup_city, sh.pickup_district, sh.pickup_state, sh.pickup_pincode),
          }}
          delivery={{
            name: sh.drop_name,
            phone: sh.drop_poc_contact_number,
            email: sh.drop_email,
            addr: addressLine(sh.drop_address, sh.drop_city, sh.drop_district, sh.drop_state, sh.drop_pincode),
          }}
          ret={{
            name: sh.return_name || sh.pickup_name,
            phone: sh.return_phone || sh.pickup_poc_contact_number,
            email: sh.return_email || sh.pickup_poc_email,
            addr: addressLine(
              sh.return_address || sh.pickup_address,
              sh.return_city || sh.pickup_city,
              sh.return_district || sh.pickup_district,
              sh.return_state || sh.pickup_state,
              sh.return_pincode || sh.pickup_pincode
            ),
          }}
        />

        </Reveal>

        {/* Items */}
        <Reveal order={4}>
        <ItemsSection packages={packages} flatItems={flatItems} labelUrl={sh.order_label_url} sh={sh} itemsTotal={itemsTotal} />

        </Reveal>

        <Reveal order={5}>
        <OrderWeightDisputeCard disputes={weightDisputes} />
        </Reveal>

        <Reveal order={6}>
        <TrackingTimeline businessStatuses={businessStatuses} />
        </Reveal>

        {/* Footer (uses latest history) */}
        <Reveal order={7}>
        <FooterRail
          lastUpdatedISO={latestHistoryISO}
          onBack={() => navigate("/all-shipments", { state: { page: fromPage } })}
          onRefetch={refetchLabel}
          onCancel={handleCancel}
          latestStatusCode={String(latestBusinessCode ?? sh?.latest_order_status_code ?? "")}
        />
        </Reveal>
      </div>

      {/* PRINT LAYOUT (hidden) */}
      <div
        ref={printRootRef}
        style={{ display: "none" }}
        className="print-root px-4 py-6"
      >
        <div className="max-w-[900px] mx-auto space-y-10">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 pdf-avoid-break">
            <div>
              <div className="text-xs text-gray-400">Logistics ▸ Shipments ▸ Details</div>
              <h1 className="text-2xl font-bold text-slate-900">Shipment Details</h1>
              <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mt-2">
                  <div className="lg:col-span-2">
                    <IdField label="Order Reference" value={sh.order_reference_number} mono copyable />
                  </div>
                  <div className="lg:col-span-2">
                    <IdField label="AWB Number" value={sh.awb_number || "-"} mono copyable />
                  </div>
                  <div className="lg:col-span-1">
                    <IdField label="Courier Partner" value={sh.allocated_carrier_partner_public_name || "-"} />
                  </div>
                  <div className="lg:col-span-1">
                    <StatusField 
                      label="Latest Status" 
                      code={latestBusinessCode ?? sh.latest_order_status_code} 
                      value={currentStatusName}
                      shipment={sh}
                      onSupportClick={handleOpenSupport}
                      showSupportForAll={true}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="text-xs text-gray-500 text-right">
              <div>Generated on: {format(new Date(), "dd MMM yyyy, hh:mm a")}</div>
              <div>Last updated: {latestHistoryISO ? formatInIST(latestHistoryISO) : "N/A"}</div>
            </div>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 gap-4 pdf-avoid-break">
            <div className="rounded-xl border p-4">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Invoice Value</div>
              <div className="text-xl font-semibold">{prettyMoney(sh.payment_details?.total_amount)}</div>
              <div className="text-xs text-slate-600 mt-1">{sh.payment_details?.payment_mode || "-"}</div>
              <ul className="text-[11px] text-slate-600 mt-1">
                <li>• Items • {flatItems.length}</li>
                <li>• Packages • {packages.length}</li>
              </ul>
            </div>
            <div className="rounded-xl border p-4">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Shipping Cost</div>
              <div className="text-xl font-semibold">{prettyMoney(sh.total_shipping_charge)}</div>
              <div className="text-xs text-slate-600 mt-1">Total Charges</div>
              <ul className="text-[11px] text-slate-600 mt-1">
                {sh.total_shipping_tax ? <li>• Tax • {prettyMoney(sh.total_shipping_tax)}</li> : null}
                {sh.awb_number ? <li>• AWB • {sh.awb_number}</li> : null}
              </ul>
            </div>
            <div className="rounded-xl border p-4">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Weight</div>
              <div className="text-xl font-semibold">
                {(() => {
                  const deadKg = Number.isFinite(Number(sh.dead_weight_in_grams)) ? Number(sh.dead_weight_in_grams) / 1000 : null;
                  const volKg = Number.isFinite(Number(sh.volumetric_weight_in_grams)) ? Number(sh.volumetric_weight_in_grams) / 1000 : null;
                  const chargedKg = deadKg == null && volKg == null ? null : Math.max(deadKg || 0, volKg || 0);
                  return chargedKg == null ? "N/A" : `${chargedKg.toFixed(2)} kg`;
                })()}
              </div>
              <div className="text-xs text-slate-600 mt-1">Dead • {gramsToKg(sh.dead_weight_in_grams)} &nbsp;&nbsp; Vol • {gramsToKg(sh.volumetric_weight_in_grams)}</div>
            </div>
            <div className="rounded-xl border p-4">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Order Date</div>
              <div className="text-base font-semibold">{sh.order_placed_date_time ? formatInIST(sh.order_placed_date_time) : "N/A"}</div>
              <ul className="text-[11px] text-slate-600 mt-1">
                {latestHistoryISO ? <li>• Updated • {latestHistoryPretty}</li> : null}
                {sh.invoice_number ? <li>• Invoice # • {sh.invoice_number}</li> : null}
              </ul>
            </div>
          </div>

          {/* Metadata */}
          <div className="space-y-3 pdf-avoid-break">
            <h2 className="text-lg font-semibold text-slate-900">Shipment Metadata</h2>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border p-3">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Order Type</div>
                <div className="text-sm font-medium">{sh.order_type || "-"}</div>
              </div>
              <div className="rounded-xl border p-3">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Pickup Org</div>
                <div className="text-sm font-medium">{sh.pickup_organisation || "-"}</div>
              </div>
              <div className="rounded-xl border p-3">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Service Type</div>
                <div className="text-sm font-medium">{sh.shipment_order_type || "-"}</div>
              </div>
              <div className="rounded-xl border p-3">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Charged Weight Category</div>
                <div className="text-sm font-medium">{(sh.charged_weight_category || "").replace(/_/g, " ") || "-"}</div>
              </div>
            </div>
          </div>

          {/* Addresses */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                title: "Pickup", rows: [
                  ["Name", sh.pickup_name],
                  ["Phone", sh.pickup_poc_contact_number],
                  ["Email", sh.pickup_poc_email],
                  ["Address", [sh.pickup_address, sh.pickup_city, sh.pickup_district, sh.pickup_state, sh.pickup_pincode].filter(Boolean).join(", ")],
                ]
              },
              {
                title: "Delivery", rows: [
                  ["Customer", sh.drop_name],
                  ["Phone", sh.drop_poc_contact_number],
                  ["Address", [sh.drop_address, sh.drop_city, sh.drop_district, sh.drop_state, sh.drop_pincode].filter(Boolean).join(", ")],
                ]
              },
              {
                title: "Return", rows: [
                  ["Name", sh.return_name || sh.pickup_name],
                  ["Phone", sh.return_phone || sh.pickup_poc_contact_number],
                  ["Address", [
                    sh.return_address || sh.pickup_address,
                    sh.return_city || sh.pickup_city,
                    sh.return_district || sh.pickup_district,
                    sh.return_state || sh.pickup_state,
                    sh.return_pincode || sh.pickup_pincode,
                  ].filter(Boolean).join(", ")],
                ]
              },
            ].map((card, idx) => (
              <div key={idx} className="rounded-xl border p-4">
                <h3 className="text-sm font-semibold text-slate-800">{card.title}</h3>
                <div className="mt-2 space-y-1.5 text-sm">
                  {card.rows.map(([k, v]) => (
                    <div key={k}><span className="text-slate-500">{k}: </span><span className="text-slate-800">{v || "N/A"}</span></div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Items */}
          <div className="space-y-2">
            <div className="flex items-end justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Items</h2>
                <div className="text-sm text-slate-500">{flatItems.length} {flatItems.length === 1 ? "item" : "items"} across {packages.length} {packages.length === 1 ? "package" : "packages"}</div>
              </div>
            </div>

            {packages.map((pkg, idx) => {
              const items = Array.isArray(pkg.items) ? pkg.items : [];
              const subtotal = items.reduce((sum, it) => sum + Number(it.quantity ?? 0) * Number(it.item_payment_details?.unit_price ?? 0), 0);
              return (
                <div key={idx} className="rounded-xl border overflow-hidden pdf-avoid-break">
                  <div className="px-4 py-2 text-sm bg-slate-50 flex items-center justify-between">
                    <div className="font-medium">Package #{idx + 1}</div>
                    <div className="text-slate-600">
                      Dead: {gramsToKg(pkg.package_dead_weight_in_grams)} • Vol: {gramsToKg(pkg.package_volumetric_weight_in_grams)}
                      {pkg.dimensions_in_cm ? ` • ${pkg.dimensions_in_cm.length}×${pkg.dimensions_in_cm.breadth}×${pkg.dimensions_in_cm.height} cm` : ""}
                    </div>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase tracking-wide text-slate-600 bg-white">
                      <tr>
                        <th className="px-4 py-2 text-left">SKU</th>
                        <th className="px-4 py-2 text-left">Item Name</th>
                        <th className="px-4 py-2 text-left">Qty</th>
                        <th className="px-4 py-2 text-left">Unit Price</th>
                        <th className="px-4 py-2 text-left">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 ? (
                        <tr><td colSpan={5} className="px-4 py-3 text-center text-slate-500">No items in this package.</td></tr>
                      ) : items.map((it, j) => {
                        const sku = it.item_general_details?.sku || "-";
                        const name = it.item_general_details?.item_name || "-";
                        const qty = Number(it.quantity ?? 0);
                        const unit = Number(it.item_payment_details?.unit_price ?? 0);
                        const total = qty * unit;
                        return (
                          <tr key={j} className="border-t">
                            <td className="px-4 py-2 font-mono text-xs">{sku}</td>
                            <td className="px-4 py-2">{name}</td>
                            <td className="px-4 py-2">{qty}</td>
                            <td className="px-4 py-2">{prettyMoney(unit)}</td>
                            <td className="px-4 py-2 font-semibold">{prettyMoney(total)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="px-4 py-2 flex justify-end">
                    <div className="min-w-[240px] rounded-lg border px-3 py-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Subtotal</span>
                        <span className="font-medium">{prettyMoney(subtotal)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {flatItems.length > 0 && (
              <div className="flex justify-end">
                <div className="min-w-[280px] rounded-xl border px-4 py-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="font-medium">{prettyMoney(itemsTotal)}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-sm">
                    <span className="text-slate-600">Shipping</span>
                    <span className="font-medium">{prettyMoney(sh.total_shipping_charge)}</span>
                  </div>
                  <div className="mt-2 pt-2 border-t flex items-center justify-between">
                    <span className="text-slate-800 font-semibold">Order Total</span>
                    <span className="text-slate-900 font-bold">
                      {prettyMoney(itemsTotal + Number(sh.total_shipping_charge ?? 0))}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* STACKED TIMELINE (latest on top) */}
          <div className="pdf-avoid-break">
            <h2 className="text-xl font-bold text-slate-900 mb-2">Shipment Tracking History</h2>
            <div className="text-sm text-slate-600 mb-4">
              Order Ref: <span className="font-medium text-slate-800">{sh.order_reference_number}</span>
              {sh.awb_number ? <> &nbsp;•&nbsp; AWB: <span className="font-medium text-slate-800">{sh.awb_number}</span></> : null}
            </div>

            <div className="space-y-3">
              {[...businessStatuses].sort((a, b) => {
                // latest first by created_date_time (fallback order_status_time)
                const aMs = Math.max(safeMs(a.created_date_time), safeMs(a.order_status_time));
                const bMs = Math.max(safeMs(b.created_date_time), safeMs(b.order_status_time));
                return bMs - aMs;
              }).map((st, i) => {
                const whenIso = st.created_date_time || st.order_status_time;
                const when = whenIso ? formatInIST(whenIso) : "N/A";
                const name = getOrderStatusName(String(st.order_status_code));
                return (
                  <div key={i} className="rounded-xl border p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="text-sm font-semibold text-slate-900">{name}</div>
                      <div className="text-xs text-slate-600 whitespace-nowrap">{when}</div>
                    </div>
                    <div className="mt-1 text-sm text-slate-700">
                      {st.order_location ? <div>Location: {st.order_location}</div> : null}
                      {st.order_remarks ? <div className="mt-0.5">{st.order_remarks}</div> : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Support Ticket Modal */}
      <CreateTicketModal
        isOpen={supportModalOpen}
        onClose={() => {
          setSupportModalOpen(false);
          setSupportOrderContext(null);
        }}
        onSuccess={(ticket) => {
          setSupportModalOpen(false);
          setSupportOrderContext(null);
          navigate(`/support/tickets/${ticket.public_id}`);
        }}
        orderContext={supportOrderContext}
      />

      {/* Utilities & print styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { height: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #c1c1c1; border-radius: 4px; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px);} to { opacity: 1; transform: translateY(0);} }
        .pdf-avoid-break { page-break-inside: avoid; }
        .print-root * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      `}</style>
    </>
  );
}

/* ============================== Components ============================== */


function IdField({ label, value, mono, badge, copyable }) {
  const onCopy = () => value && navigator.clipboard?.writeText?.(String(value));
  return (
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 flex items-center gap-2">
        <span title={value || ''} className={`${mono ? 'font-mono' : 'font-medium'} text-slate-900 break-all`}>
          {value || '-'}
        </span>
        {copyable && value && (
          <button onClick={onCopy} className="text-[11px] px-1.5 py-0.5 rounded border hover:bg-slate-50">Copy</button>
        )}
      </div>
    </div>
  );
}

function StatusField({ label, code, value, shipment, onSupportClick, showSupportForAll = false }) {
  const tone = statusPillTone(code);
  const statusCodeStr = String(code ?? "");
  const needsSupport = showSupportForAll || STATUSES_REQUIRING_SUPPORT.includes(statusCodeStr);

  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 flex items-center gap-1.5">
        <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${tone}`}>{value || "-"}</div>
        {needsSupport && shipment && onSupportClick && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSupportClick();
            }}
            className="inline-flex items-center justify-center p-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-all shadow-sm hover:shadow-md"
            title="Create Support Ticket"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

/* -------- Identity bar -------- */
function IdentityBar({
  sh,
  latestBusinessCode,
  currentStatusName,
  onRefetchLabel,
  onCancel,
  labelError,
  onExportPDF,
  openDamageClaim,
  openWeightDispute,
  claimModalOpen,
  setClaimModalOpen,
  shipment,
  claimModalType,
  onSupportClick,
  onDownloadCarrierImage,
  carrierImageLoading,
  businessStatuses = [],
}) {
  const statusCode = String(latestBusinessCode ?? sh.latest_order_status_code ?? "");
  const isPickupPending = PICKUP_PENDING_CODES.includes(statusCode);

  // The page states its state: the header's edge takes the tone of the CURRENT status.
  const toneStr = statusPillTone(latestBusinessCode ?? sh.latest_order_status_code);
  const edgeTone = toneStr.includes("rose") ? "bg-rose-400" : toneStr.includes("emerald") ? "bg-emerald-400" : "bg-sky-400";
  const dotTone = toneStr.includes("rose") ? "bg-rose-500" : toneStr.includes("emerald") ? "bg-emerald-500" : "bg-sky-500";

  const paymentMode = String(sh.payment_details?.payment_mode || sh.order_type || "").toUpperCase();
  const isCod = paymentMode.includes("COD");
  const deadKg = Number.isFinite(Number(sh.dead_weight_in_grams)) ? Number(sh.dead_weight_in_grams) / 1000 : null;
  const volKg = Number.isFinite(Number(sh.volumetric_weight_in_grams)) ? Number(sh.volumetric_weight_in_grams) / 1000 : null;
  const chargedKg = deadKg == null && volKg == null ? null : Math.max(deadKg || 0, volKg || 0);
  const trackingUrl = sh.awb_number ? `https://tracking.wayuvega.in/track?awb=${encodeURIComponent(sh.awb_number)}` : null;
  const shipmentKind = String(sh.shipment_order_type || "").toUpperCase();

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      {/* status edge — one glance = one state */}
      <div className={`h-1 ${edgeTone}`} />

      {/* identity */}
      <div className="px-5 pb-4 pt-5 md:px-6">
        <div className="flex items-center justify-between gap-3">
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">
            {shipmentKind ? `${shipmentKind} Shipment` : "Shipment"}
          </div>
          {onSupportClick ? (
            <button
              onClick={onSupportClick}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700"
              title="Create Support Ticket"
            >
              <MessageSquare className="h-3.5 w-3.5" />
              Support
            </button>
          ) : null}
        </div>

        <div className="mt-1 flex flex-wrap items-center gap-2">
          <h1 className="font-mono text-[28px] font-bold leading-tight tracking-tight text-slate-900 md:text-[32px]">
            {sh.awb_number || sh.order_reference_number || "-"}
          </h1>
          {sh.awb_number ? <HeaderCopy value={sh.awb_number} label="AWB" /> : null}
        </div>

        {/* fact chips */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {latestBusinessCode ? (
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${statusPillTone(latestBusinessCode)}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${dotTone}`} aria-hidden />
              {currentStatusName}
            </span>
          ) : null}
          {paymentMode ? (
            <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${isCod ? "bg-amber-50 text-amber-700 ring-amber-200" : "bg-indigo-50 text-indigo-700 ring-indigo-200"}`}>
              {paymentMode}
            </span>
          ) : null}
          {sh.allocated_carrier_partner_public_name ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700 ring-1 ring-sky-200">
              <Truck className="h-3.5 w-3.5" />
              {sh.allocated_carrier_partner_public_name}
            </span>
          ) : null}
          {chargedKg != null ? (
            <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
              {chargedKg.toFixed(2)} kg
            </span>
          ) : null}
        </div>

        <div className="mt-2.5 flex items-center gap-1.5 text-sm text-slate-500">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Order ref</span>
          <span className="truncate font-mono font-medium text-slate-700" title={sh.order_reference_number || undefined}>
            {sh.order_reference_number || "-"}
          </span>
          {sh.order_reference_number ? <HeaderCopy value={sh.order_reference_number} label="Order ref" /> : null}
        </div>
      </div>

      {/* toolbar — intent groups */}
      <div className="flex flex-wrap items-start gap-x-8 gap-y-4 border-t border-slate-100 bg-gradient-to-r from-slate-50 to-indigo-50/40 px-5 py-4 md:px-6">
        <div>
          <div className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">Documents</div>
          <div className="flex flex-wrap gap-2">
            <ActionBtn
              as="a"
              href={sh.order_label_url || undefined}
              disabled={!sh.order_label_url}
              title={!sh.order_label_url ? "Label not available yet" : "Open the shipping label"}
              icon={<Tag className="h-4 w-4" />}
            >
              View Label
            </ActionBtn>
            {isPickupPending ? (
              <ActionBtn onClick={onRefetchLabel} icon={<RefreshCw className="h-4 w-4" />}>
                Refetch Label
              </ActionBtn>
            ) : null}
            {inCarrierNetwork(statusCode) ? (
              <>
                <ActionBtn
                  onClick={() => onDownloadCarrierImage?.("qc", "QC image")}
                  disabled={!sh.awb_number || carrierImageLoading === "qc"}
                  title={!sh.awb_number ? "AWB number is required" : "Download QC image"}
                  icon={<Camera className="h-4 w-4" />}
                >
                  {carrierImageLoading === "qc" ? "Downloading…" : "QC Image"}
                </ActionBtn>
                <ActionBtn
                  onClick={() => onDownloadCarrierImage?.("sorter", "Sorter image")}
                  disabled={!sh.awb_number || carrierImageLoading === "sorter"}
                  title={!sh.awb_number ? "AWB number is required" : "Download sorter image"}
                  icon={<Layers className="h-4 w-4" />}
                >
                  {carrierImageLoading === "sorter" ? "Downloading…" : "Sorter Image"}
                </ActionBtn>
              </>
            ) : null}
            {isDeliveredState(statusCode) ? (
              <ActionBtn
                onClick={() => onDownloadCarrierImage?.("epod", "Proof of delivery")}
                disabled={!sh.awb_number || carrierImageLoading === "epod"}
                title={!sh.awb_number ? "AWB number is required" : "Download proof of delivery"}
                icon={<FileCheck className="h-4 w-4" />}
              >
                {carrierImageLoading === "epod" ? "Downloading…" : "EPOD"}
              </ActionBtn>
            ) : null}
          </div>
        </div>

        <div>
          <div className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">Claims</div>
          <div className="flex flex-wrap gap-2">
            <ActionBtn onClick={openDamageClaim} variant="warnOutline" icon={<ShieldAlert className="h-4 w-4" />}>
              Raise Damage Claim
            </ActionBtn>
            <ActionBtn onClick={openWeightDispute} variant="successOutline" icon={<Video className="h-4 w-4" />}>
              Upload Packaging Video
            </ActionBtn>
          </div>
        </div>

        {isCancellable(statusCode) ? (
          <div>
            <div className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-rose-300">Order</div>
            <div className="flex flex-wrap gap-2">
              <ActionBtn onClick={onCancel} variant="danger" icon={<XCircle className="h-4 w-4" />}>
                Cancel Shipment
              </ActionBtn>
            </div>
          </div>
        ) : null}
      </div>

      {/* customer tracking link — the shareable artifact */}
      {trackingUrl ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-5 py-3.5 md:px-6">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Customer tracking link</div>
            <a
              href={trackingUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-0.5 inline-flex max-w-full items-center gap-1.5 font-mono text-sm text-indigo-600 hover:text-indigo-800"
            >
              <span className="truncate">{trackingUrl}</span>
              <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
            </a>
          </div>
          <CopyLinkButton value={trackingUrl} />
        </div>
      ) : null}

      {/* journey rail */}
      <MilestoneRail businessStatuses={businessStatuses} />

      <ClaimUploadModal
        open={claimModalOpen}
        onClose={() => setClaimModalOpen(false)}
        shipment={shipment}
        claimType={claimModalType}
      />
      {labelError && <p className="px-5 pb-3 text-xs text-rose-600 md:px-6">{labelError}</p>}
    </section>
  );
}

/* "Copy link" with a copied beat — the band's single action. */
function CopyLinkButton({ value }) {
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={onCopy}
      className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-all duration-200 ${
        copied ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
      }`}
    >
      {copied ? <CheckIcon className="h-4 w-4" /> : <CopyIcon className="h-4 w-4" />}
      {copied ? "Copied" : "Copy link"}
    </button>
  );
}

/* The journey at a glance — forward or RTO story, with a terminal band when the story has ended. */
function MilestoneRail({ businessStatuses = [] }) {
  const reduce = useReducedMotion();
  const seen = new Set(businessStatuses.map((st) => String(st.order_status_code || "")));
  const isRto = RTO_CODES.some((c) => seen.has(c));
  const cancelledCode = CANCEL_FAMILY.find((c) => seen.has(c));
  const stages = isRto ? RTO_STAGES : JOURNEY_STAGES;

  const doneFlags = stages.map((stage) => stage.codes.some((c) => seen.has(c)));
  let lastDone = -1;
  doneFlags.forEach((d, i) => { if (d) lastDone = i; });

  // Terminal band facts (cancelled / lost / damaged / delivered / returned)
  const lostTs = seen.has(OrderBusinessStatusEnum.LOST.code) ? latestTsFor(businessStatuses, [OrderBusinessStatusEnum.LOST.code]) : null;
  const damagedTs = seen.has(OrderBusinessStatusEnum.DAMAGED.code) ? latestTsFor(businessStatuses, [OrderBusinessStatusEnum.DAMAGED.code]) : null;
  const deliveredTs = !isRto && seen.has(OrderBusinessStatusEnum.DELIVERED.code) ? latestTsFor(businessStatuses, [OrderBusinessStatusEnum.DELIVERED.code]) : null;
  const returnedTs = isRto && seen.has(OrderBusinessStatusEnum.RTO_DELIVERED.code) ? latestTsFor(businessStatuses, [OrderBusinessStatusEnum.RTO_DELIVERED.code]) : null;
  const cancelPendingTs = seen.has(OrderBusinessStatusEnum.ORDER_CANCELLATION_PENDING.code) && !cancelledCode
    ? latestTsFor(businessStatuses, [OrderBusinessStatusEnum.ORDER_CANCELLATION_PENDING.code]) : null;

  const CANCEL_COPY = {
    [OrderBusinessStatusEnum.CANCELLED_ORDER.code]: "Cancelled",
    [OrderBusinessStatusEnum.PICKUP_CANCELLED_BY_CARRIER.code]: "Pickup cancelled by courier",
    [OrderBusinessStatusEnum.PICKUP_CANCELLED_BY_SHIPPER.code]: "Pickup cancelled",
    [OrderBusinessStatusEnum.ORDER_CANCELLED_BY_END_CUSTOMER_DURING_CONFIRMATION.code]: "Cancelled during confirmation",
  };

  const Band = ({ tone, icon, children }) => (
    <div
      className={`flex items-center gap-2 border-t px-5 py-3 text-sm font-medium md:px-6 ${
        tone === "rose"
          ? "border-rose-100 bg-rose-50/70 text-rose-700"
          : tone === "amber"
            ? "border-amber-100 bg-amber-50/70 text-amber-800"
            : "border-emerald-100 bg-emerald-50/70 text-emerald-700"
      }`}
    >
      {icon}
      <span>{children}</span>
    </div>
  );

  // Cancelled: the journey never really happened — band alone tells the whole story.
  if (cancelledCode) {
    const ts = latestTsFor(businessStatuses, [cancelledCode]);
    return (
      <Band tone="rose" icon={<XCircle className="h-4 w-4 flex-shrink-0" />}>
        <span className="font-bold">{CANCEL_COPY[cancelledCode] || "Cancelled"}</span>
        {ts ? <span className="font-normal"> on {fmtDayTime(ts)}</span> : null}
      </Band>
    );
  }

  const rail = lastDone === -1 ? null : (
    <div className="border-t border-slate-100 bg-slate-50/50 px-5 pb-4 pt-5 md:px-6">
      <div className="flex items-start">
        {stages.map((stage, i) => {
          const done = i < lastDone;
          const current = i === lastDone;
          const finalStage = current && i === stages.length - 1;
          const coin = done || current;
          const coinColor = isRto ? "bg-amber-500" : "bg-blue-500";
          const coinRing = isRto ? "ring-amber-100" : "ring-blue-100";
          return (
            <React.Fragment key={stage.label}>
              {i > 0 ? (
                <div className={`mt-[13px] h-0.5 min-w-4 flex-1 rounded-full ${i <= lastDone ? (isRto ? "bg-amber-500" : "bg-blue-500") : "bg-slate-200"}`} />
              ) : null}
              <div className="flex flex-col items-center gap-1.5 px-1">
                <span
                  className={`relative flex h-7 w-7 items-center justify-center rounded-full transition-colors ${
                    finalStage
                      ? "bg-emerald-500 text-white shadow-sm ring-4 ring-emerald-100"
                      : coin
                        ? `${coinColor} text-white shadow-sm ${current ? `ring-4 ${coinRing}` : ""}`
                        : "border-2 border-slate-200 bg-white text-slate-300"
                  }`}
                >
                  {coin ? <CheckIcon className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
                  {current && !finalStage && !reduce ? (
                    <span className={`absolute inset-0 animate-ping rounded-full opacity-30 ${isRto ? "bg-amber-400" : "bg-blue-400"}`} />
                  ) : null}
                </span>
                <span className={`whitespace-nowrap text-[11px] ${coin ? "font-semibold text-slate-700" : "font-medium text-slate-400"}`}>
                  {stage.label}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      {rail}
      {cancelPendingTs ? (
        <Band tone="amber" icon={<Clock className="h-4 w-4 flex-shrink-0" />}>
          <span className="font-bold">Cancellation requested</span>
          <span className="font-normal"> on {fmtDayTime(cancelPendingTs)}</span>
        </Band>
      ) : null}
      {lostTs ? (
        <Band tone="rose" icon={<AlertTriangle className="h-4 w-4 flex-shrink-0" />}>
          <span className="font-bold">Shipment lost</span>
          <span className="font-normal"> — reported on {fmtDayTime(lostTs)}. Raise a claim from the Claims actions above.</span>
        </Band>
      ) : null}
      {damagedTs ? (
        <Band tone="rose" icon={<AlertTriangle className="h-4 w-4 flex-shrink-0" />}>
          <span className="font-bold">Shipment damaged</span>
          <span className="font-normal"> — reported on {fmtDayTime(damagedTs)}. Raise a damage claim from the Claims actions above.</span>
        </Band>
      ) : null}
      {deliveredTs ? (
        <Band tone="emerald" icon={<CheckIcon className="h-4 w-4 flex-shrink-0" />}>
          <span className="font-bold">Delivered</span>
          <span className="font-normal"> on {fmtDayTime(deliveredTs)}</span>
        </Band>
      ) : null}
      {returnedTs ? (
        <Band tone="amber" icon={<RotateCcw className="h-4 w-4 flex-shrink-0" />}>
          <span className="font-bold">Returned to you</span>
          <span className="font-normal"> on {fmtDayTime(returnedTs)}</span>
        </Band>
      ) : null}
    </>
  );
}

/* Tiny copy affordance/* Page-load choreography: each section rises in, in reading order. Honors reduced motion. */
function Reveal({ order = 0, children }) {
  const reduce = useReducedMotion();
  if (reduce) return children;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.38, delay: order * 0.07, ease: [0.22, 0.61, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

/* Tiny copy affordance for header identifiers — flips to a check for a beat. */
function HeaderCopy({ value, label }) {
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    navigator.clipboard?.writeText(String(value));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  };
  return (
    <button
      onClick={onCopy}
      className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
      title={`Copy ${label}`}
      aria-label={`Copy ${label}`}
    >
      {copied ? <CheckIcon className="h-3.5 w-3.5 text-emerald-600" /> : <CopyIcon className="h-3.5 w-3.5" />}
    </button>
  );
}

/* One button system for the identity bar: a single accent, neutral secondaries, outlined intents. */
function ActionBtn({ as, href, onClick, disabled, title, icon, variant = "neutral", children }) {
  const base =
    "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors";
  const variants = {
    primary: disabled
      ? "bg-indigo-300 text-white cursor-not-allowed"
      : "bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm",
    neutral: disabled
      ? "border border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
      : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300",
    danger: disabled
      ? "border border-rose-200 bg-rose-50 text-rose-300 cursor-not-allowed"
      : "border border-rose-200 bg-white text-rose-600 hover:bg-rose-50",
    warnOutline: "border border-amber-300 bg-amber-50/60 text-amber-800 hover:bg-amber-100",
    successOutline: "border border-emerald-300 bg-emerald-50/60 text-emerald-800 hover:bg-emerald-100",
  };
  const cls = `${base} ${variants[variant] ?? variants.neutral}`;
  if (as === "a" && !disabled) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={cls} title={title}>
        {icon}
        {children}
      </a>
    );
  }
  return (
    <button onClick={onClick} disabled={disabled} className={cls} title={title}>
      {icon}
      {children}
    </button>
  );
}

/* -------- Charges: the money ledger — invoice, COD collectible, freight, total, settlement -------- */
function ChargesCard({ sh, walletType }) {
  const isCod = String(sh.payment_details?.payment_mode || sh.order_type || "").toUpperCase().includes("COD");
  const invoiceValue = Number(sh.payment_details?.total_amount ?? 0);
  const freight = Number(sh.total_shipping_charge ?? 0);
  const tax = Number(sh.total_shipping_tax ?? 0);

  const settlement =
    walletType && walletType.includes("NETOFF")
      ? { pill: "COD Netoff", note: "Freight is adjusted against your COD remittance — you receive COD minus freight." }
      : walletType && walletType.includes("POST")
        ? { pill: "Postpaid", note: "Freight is billed to your postpaid account." }
        : walletType && walletType.includes("PRE")
          ? { pill: "Prepaid Wallet", note: "Freight is charged from your wallet." }
          : null;

  const Row = ({ label, value, strong, muted }) => (
    <div className={`flex items-baseline justify-between gap-3 ${strong ? "text-[15px] font-bold text-slate-900" : muted ? "text-sm text-slate-500" : "text-sm text-slate-700"}`}>
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );

  return (
    <div className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
          <CreditCard className="h-4 w-4" />
        </span>
        <span className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-wider text-slate-400">Shipment Charges</span>
        {settlement ? (
          <span className="ml-auto whitespace-nowrap rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            {settlement.pill}
          </span>
        ) : null}
      </div>

      <div className="mt-3 space-y-2">
        {isCod ? (
          <div className="flex items-baseline justify-between gap-3 rounded-lg bg-amber-50/80 px-2.5 py-1.5 text-sm font-semibold text-amber-800">
            <span>COD to collect</span>
            <span className="tabular-nums">{prettyMoney(invoiceValue)}</span>
          </div>
        ) : null}
        <Row label={tax ? `Freight (incl. tax ${prettyMoney(tax)})` : "Freight"} value={prettyMoney(freight)} />
        <div className="border-t border-slate-100 pt-2">
          <Row label="Total charge" value={prettyMoney(freight)} strong />
        </div>
      </div>

      {settlement ? (
        <div className="mt-auto pt-3">
          <div className="flex items-start gap-1.5 border-t border-slate-100 pt-2.5 text-xs leading-relaxed text-slate-500">
            <WalletIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
            <span>{settlement.note}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* -------- Metric tile (supports inlineValue) -------- */
function MetricTile({ label, value, icon, tint = "bg-slate-100 text-slate-500", hintPill, meta, compactValue = false }) {
  return (
    <div className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
      {/* label row — one line, nothing competes with it */}
      <div className="flex items-center gap-2">
        {icon ? <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg ${tint}`}>{icon}</span> : null}
        <span className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</span>
        {hintPill ? (
          <span className="ml-auto whitespace-nowrap rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            {hintPill}
          </span>
        ) : null}
      </div>

      <div className={`${compactValue ? "text-lg" : "text-[26px]"} mt-2.5 font-bold leading-tight tracking-tight tabular-nums text-slate-900`}>
        {value}
      </div>

      {meta ? (
        <div className="mt-auto pt-3">
          <div className="border-t border-slate-100 pt-2.5 text-xs leading-relaxed text-slate-500">{meta}</div>
        </div>
      ) : null}
    </div>
  );
}

/* -------- Weights tile: the whole story — dead, volumetric (with dims), and which one billed -------- */
function KpiWeightsCompact({ deadGrams, volGrams, chargedCategory, dims }) {
  const deadKg = Number.isFinite(Number(deadGrams)) ? Number(deadGrams) / 1000 : null;
  const volKg = Number.isFinite(Number(volGrams)) ? Number(volGrams) / 1000 : null;
  const chargedKg = deadKg == null && volKg == null ? null : Math.max(deadKg || 0, volKg || 0);

  // Which weight was billed: trust the server category; fall back to the larger value.
  const cat = String(chargedCategory || "").toUpperCase();
  const basis = cat.includes("VOL") ? "vol" : cat.includes("DEAD") ? "dead" : (volKg || 0) > (deadKg || 0) ? "vol" : "dead";

  // Shipment-level dimensions from the search-orders response (shipment_package_dimensions).
  const dimsLabel =
    dims && dims.length != null && dims.breadth != null && dims.height != null
      ? `${dims.length} × ${dims.breadth} × ${dims.height} ${dims.unit || "cm"}`
      : null;

  const WeightRow = ({ name, kg, charged, suffix }) => (
    <div className={`flex items-baseline justify-between gap-2 ${charged ? "font-semibold text-slate-800" : "text-slate-500"}`}>
      <span className="flex items-center gap-1.5">
        {charged ? <span className="h-1.5 w-1.5 rounded-full bg-violet-500" aria-hidden /> : <span className="h-1.5 w-1.5" aria-hidden />}
        {name}
        {suffix ? <span className="font-normal text-slate-400">{suffix}</span> : null}
      </span>
      <span className="tabular-nums">{kg == null ? "N/A" : `${kg.toFixed(2)} kg`}</span>
    </div>
  );

  return (
    <MetricTile
      label="Charged Weight"
      icon={<ScaleIcon className="h-4 w-4" />}
      tint="bg-violet-50 text-violet-600"
      hintPill={basis === "vol" ? "Volumetric" : "Dead"}
      value={chargedKg == null ? "N/A" : `${chargedKg.toFixed(2)} kg`}
      meta={
        <div className="space-y-1" title="Billing uses the higher of dead (scale) weight and volumetric (dimensional) weight.">
          <WeightRow name="Dead" kg={deadKg} charged={basis === "dead"} />
          <WeightRow name="Volumetric" kg={volKg} charged={basis === "vol"} suffix={dimsLabel ? `· ${dimsLabel}` : null} />
        </div>
      }
    />
  );
}

/* -------- Metadata: one quiet sheet — small-caps labels, weighted values, mono refs -------- */
function MetadataGrid({ title, items }) {
  const clean = (items || []).filter(Boolean);
  if (clean.length === 0) return null;
  // Tailwind needs literal class names — no template interpolation.
  const COLS = { 1: "lg:grid-cols-1", 2: "lg:grid-cols-2", 3: "lg:grid-cols-3", 4: "lg:grid-cols-4", 5: "lg:grid-cols-5", 6: "lg:grid-cols-6" };
  const lgCols = COLS[Math.min(clean.length, 6)];
  return (
    <section>
      <SectionHeader title={title} icon={<Info className="h-5 w-5" />} tint="bg-gradient-to-br from-sky-50 to-sky-100 text-sky-600" />
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <dl className={`grid grid-cols-1 divide-y divide-slate-100 sm:grid-cols-2 sm:divide-y lg:divide-y-0 lg:divide-x ${lgCols}`}>
          {clean.map(({ k, v, mono }, i) => (
            <div key={i} className="px-5 py-4">
              <dt className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{k}</dt>
              {mono ? (
                <dd className="mt-1.5 flex min-w-0 items-start gap-1">
                  <span className="break-all font-mono text-[13px] font-medium leading-snug text-slate-800">{v || "-"}</span>
                  {v ? <HeaderCopy value={v} label={k} /> : null}
                </dd>
              ) : (
                <dd className="mt-1.5 text-sm font-semibold capitalize text-slate-800">
                  {String(v || "-").toLowerCase()}
                </dd>
              )}
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

/* -------- Addresses: one route sheet — pickup → delivery, return collapses when identical -------- */
function ResponsiveAddresses({ pickup, delivery, ret }) {
  const norm = (x) => String(x || "").trim().toLowerCase();
  const returnSameAsPickup =
    norm(ret?.addr) === norm(pickup?.addr) && norm(ret?.phone) === norm(pickup?.phone);

  const ContactBlock = ({ role, icon, tint, person }) => (
    <div className="min-w-0 flex-1 px-5 py-5">
      <div className="flex items-center gap-2">
        <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${tint}`}>{icon}</span>
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{role}</span>
      </div>

      <div className="mt-2.5 text-sm font-bold text-slate-900">{person?.name || "N/A"}</div>

      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-slate-600">
        {person?.phone ? (
          <span className="inline-flex items-center gap-1">
            <Phone className="h-3 w-3 text-slate-400" />
            <a href={`tel:${person.phone}`} className="hover:text-indigo-600">{person.phone}</a>
            <HeaderCopy value={person.phone} label="phone" />
          </span>
        ) : null}
        {person?.email ? (
          <span className="inline-flex min-w-0 items-center gap-1">
            <Mail className="h-3 w-3 flex-shrink-0 text-slate-400" />
            <a href={`mailto:${person.email}`} className="truncate hover:text-indigo-600" title={person.email}>
              {person.email}
            </a>
          </span>
        ) : null}
      </div>

      <p className="mt-2 text-[13px] leading-relaxed text-slate-500">{person?.addr || "N/A"}</p>
    </div>
  );

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-col divide-y divide-slate-100 lg:flex-row lg:divide-x lg:divide-y-0">
        <ContactBlock role="Pickup" icon={<MapPin className="h-4 w-4 text-blue-600" />} tint="bg-blue-50" person={pickup} />

        {/* route connector — the journey reads left → right */}
        <div className="relative hidden w-0 lg:block">
          <span className="absolute left-0 top-1/2 z-10 flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 shadow-sm">
            <MoveRight className="h-3.5 w-3.5" />
          </span>
        </div>

        <ContactBlock role="Delivery" icon={<Home className="h-4 w-4 text-emerald-600" />} tint="bg-emerald-50" person={delivery} />

        {!returnSameAsPickup ? (
          <ContactBlock role="Return" icon={<RotateCcw className="h-4 w-4 text-amber-600" />} tint="bg-amber-50" person={ret} />
        ) : null}
      </div>

      {returnSameAsPickup ? (
        <div className="flex items-center gap-2 border-t border-slate-100 bg-slate-50/60 px-5 py-2.5 text-xs text-slate-500">
          <RotateCcw className="h-3.5 w-3.5 text-amber-500" />
          Return address is the same as pickup
        </div>
      ) : null}
    </section>
  );
}

/* -------- Section header / empty state -------- */
function SectionHeader({ title, subtitle, right, icon, tint = "bg-gradient-to-br from-slate-100 to-slate-200 text-slate-600" }) {
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        {icon ? (
          <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl ${tint}`}>
            {icon}
          </div>
        ) : null}
        <div>
          <h2 className="text-lg font-bold text-slate-900">{title}</h2>
          {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {right}
    </div>
  );
}
function EmptyState({ label }) {
  return <div className="rounded-xl border border-dashed p-6 text-center text-sm text-slate-500 bg-slate-50">{label}</div>;
}

/* -------- Packages & items: goods only — money lives in the Charges card above -------- */
function ItemsSection({ packages, flatItems, labelUrl, sh, itemsTotal }) {
  // Shipment-level weight/dims describe the (single) package; per-package fields don't exist on this API.
  const dims = sh?.shipment_package_dimensions;
  const dimsLabel =
    dims && dims.length != null ? `${dims.length}×${dims.breadth}×${dims.height} ${dims.unit || "cm"}` : null;
  const deadLabel =
    sh?.dead_weight_in_grams != null ? `${Number(sh.dead_weight_in_grams)}g` : null;
  const singleBandMeta = [deadLabel, dimsLabel].filter(Boolean).join("  ·  ");

  return (
    <section>
      <SectionHeader
        title="Packages & items"
        icon={<PackageIcon className="h-5 w-5" />}
        tint="bg-gradient-to-br from-indigo-50 to-indigo-100 text-indigo-600"
        subtitle={`${flatItems.length} ${flatItems.length === 1 ? "item" : "items"} · ${packages.length} ${packages.length === 1 ? "package" : "packages"}`}
        right={
          labelUrl ? (
            <a
              href={labelUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100"
            >
              <Tag className="h-4 w-4" />
              View Label
            </a>
          ) : null
        }
      />

      {packages.length === 0 ? (
        <EmptyState label="No package/items found for this shipment." />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <tbody>
                {packages.map((pkg, pkgIdx) => {
                  const items = Array.isArray(pkg.items) ? pkg.items : [];
                  const bandMeta =
                    packages.length === 1
                      ? singleBandMeta
                      : pkg.dimensions_in_cm
                        ? `${pkg.dimensions_in_cm.length}×${pkg.dimensions_in_cm.breadth}×${pkg.dimensions_in_cm.height} cm`
                        : "";
                  return (
                    <React.Fragment key={pkgIdx}>
                      {/* package band */}
                      <tr>
                        <td colSpan={4} className="border-b border-slate-100 bg-slate-50/60 px-5 py-2.5">
                          <div className="flex items-baseline justify-between gap-3">
                            <span className="text-sm font-bold text-slate-800">Package {pkgIdx + 1}</span>
                            {bandMeta ? (
                              <span className="font-mono text-xs text-slate-400">{bandMeta}</span>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                      {/* column heads per package block */}
                      <tr className="border-b border-slate-100 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                        <th className="px-5 py-2 font-semibold">Item</th>
                        <th className="w-20 px-4 py-2 text-right font-semibold">Qty</th>
                        <th className="w-32 px-4 py-2 text-right font-semibold">Unit Price</th>
                        <th className="w-36 px-5 py-2 text-right font-semibold">Total</th>
                      </tr>
                      {items.map((it, j) => {
                        const sku = it.item_general_details?.sku;
                        const name = it.item_general_details?.item_name || "-";
                        const qty = Number(it.quantity ?? 0);
                        const unit = Number(it.item_payment_details?.unit_price ?? 0);
                        return (
                          <tr key={`${pkgIdx}-${j}`} className="border-b border-slate-50 text-sm transition-colors last:border-b-0 hover:bg-slate-50/60">
                            <td className="px-5 py-3">
                              <div className="font-medium text-slate-900">{name}</div>
                              {sku ? <div className="mt-0.5 font-mono text-[11px] text-slate-400">SKU {sku}</div> : null}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums text-slate-700">{qty}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-slate-700">{prettyMoney(unit)}</td>
                            <td className="px-5 py-3 text-right font-semibold tabular-nums text-slate-900">{prettyMoney(qty * unit)}</td>
                          </tr>
                        );
                      })}
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-5 py-4 text-center text-sm text-slate-400">No items in this package.</td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
              {flatItems.length > 0 ? (
                <tfoot>
                  <tr className="border-t border-slate-100">
                    <td colSpan={2} />
                    <td className="px-4 py-3 text-right text-sm font-semibold text-slate-600">Items subtotal</td>
                    <td className="px-5 py-3 text-right text-sm font-bold tabular-nums text-slate-900">{prettyMoney(itemsTotal)}</td>
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

/* -------- Tracking history  -------- */
function TrackingTimeline({ businessStatuses = [] }) {
  const [filter, setFilter] = React.useState("ALL");
  if (!businessStatuses.length) {
    return (
      <section className="space-y-3">
        <SectionHeader title="Shipment Tracking History" icon={<Radio className="h-5 w-5" />} tint="bg-gradient-to-br from-emerald-50 to-emerald-100 text-emerald-600" />
        <EmptyState label="No tracking statuses found." />
      </section>
    );
  }

  // normalize + sort latest → oldest
  const rows = [...businessStatuses]
    .map(s => ({
      ...s,
      whenMs: Math.max(safeMs(s.created_date_time), safeMs(s.order_status_time)),
      whenISO: s.created_date_time || s.order_status_time,
    }))
    .sort((a, b) => b.whenMs - a.whenMs);

  const passes = (s) => {
    if (filter === "ALL") return true;
    const code = String(s.order_status_code || "");
    if (filter === "EXC") return ["110", "120", "170"].includes(code);          // cancelled/failed/rtx
    if (filter === "LOC") return Boolean(s.order_location);
    if (filter === "ATT") return /attempt/i.test(s.order_remarks || "");
    return true;
  };

  const filtered = rows.filter(passes);

  // group by calendar day
  const groups = filtered.reduce((acc, s) => {
    const d = s.whenISO ? format(parseISO(s.whenISO), "EEE, dd MMM yyyy") : "Unknown";
    (acc[d] ||= []).push(s);
    return acc;
  }, {});

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <SectionHeader title="Shipment Tracking History" icon={<Radio className="h-5 w-5" />} tint="bg-gradient-to-br from-emerald-50 to-emerald-100 text-emerald-600" />

        {/* filters — segmented control */}
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg bg-slate-100 p-0.5">
            {[
              ["ALL", "All"],
              ["EXC", "Exceptions"],
              ["LOC", "Location"],
              ["ATT", "Attempts"],
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`rounded-md px-2.5 py-1 text-xs transition-all duration-150 ${
                  filter === key
                    ? "bg-white font-semibold text-slate-900 shadow-sm"
                    : "font-medium text-slate-500 hover:text-slate-700"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <CopyHistoryButton items={filtered} />
        </div>
      </div>

      {/* log-book v2: gradient rail, floating day pills, milestone markers, staggered entrance */}
      <div className="max-h-[560px] overflow-y-auto overscroll-contain rounded-2xl border border-slate-200 bg-white shadow-sm">
        {Object.entries(groups).map(([day, list], groupIdx) => (
          <div key={day} className="relative">
            {/* day pill floats on the rail — sticks while its day scrolls */}
            <div className="sticky top-0 z-20 flex items-center gap-2 bg-white/95 px-5 pb-1 pt-4 backdrop-blur-sm">
              <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 shadow-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-indigo-400" aria-hidden />
                <span className="text-[11px] font-bold uppercase tracking-wide text-slate-600">{day}</span>
                <span className="text-[11px] text-slate-400">{list.length}</span>
              </span>
            </div>

            <ul className="relative pb-2 pt-1">
              {/* rail fades in and out of each day block */}
              <span
                className="absolute bottom-0 left-[27px] top-0 w-px bg-gradient-to-b from-transparent via-slate-200 to-transparent"
                aria-hidden
              />
              {list.map((st, idx) => {
                const isLatest = groupIdx === 0 && idx === 0;
                const name = getOrderStatusName(String(st.order_status_code));
                const code = String(st.order_status_code || "");
                const isException = ["110", "120", "170"].includes(code);
                const isDelivered = code === "100";
                const when = st.whenISO ? formatInIST(st.whenISO) : "N/A";
                const rowIndex = groupIdx * 10 + idx;

                return (
                  <TimelineRow key={st.whenISO + idx} order={rowIndex}>
                    <li
                      className={`group relative py-3 pl-12 pr-5 transition-colors ${
                        isLatest ? "bg-gradient-to-r from-sky-50/70 to-transparent" : "hover:bg-slate-50/60"
                      }`}
                    >
                      {/* marker: milestones get an icon coin, routine updates a dot */}
                      {isException || isDelivered ? (
                        <span
                          className={`absolute left-[27px] top-[16px] flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full text-white shadow-sm ring-4 ring-white ${
                            isException ? "bg-rose-500" : "bg-emerald-500"
                          }`}
                        >
                          {isException ? <AlertTriangle className="h-3 w-3" /> : <CheckIcon className="h-3 w-3" />}
                        </span>
                      ) : (
                        <span
                          className={`absolute left-[27px] top-[22px] h-2.5 w-2.5 -translate-x-1/2 rounded-full ring-4 ring-white ${
                            isLatest ? "bg-sky-500" : "bg-slate-300"
                          }`}
                        >
                          {isLatest ? (
                            <span className="absolute inset-0 animate-ping rounded-full bg-sky-400 opacity-40" />
                          ) : null}
                        </span>
                      )}

                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <span className={`text-sm font-semibold ${isException ? "text-rose-700" : "text-slate-900"}`}>{name}</span>
                        {isLatest ? (
                          <span className="rounded-full bg-sky-100 px-2 py-px text-[10px] font-bold uppercase tracking-wide text-sky-700">
                            Latest
                          </span>
                        ) : null}
                        {isException ? (
                          <span className="rounded-full bg-rose-50 px-2 py-px text-[11px] font-semibold text-rose-700 ring-1 ring-rose-200">
                            Exception
                          </span>
                        ) : null}
                        <span className="ml-auto whitespace-nowrap text-xs tabular-nums text-slate-400">{when}</span>
                      </div>

                      {(st.order_location || st.order_remarks) ? (
                        <div className="mt-0.5 text-[13px] leading-snug text-slate-500">
                          {st.order_location ? (
                            <span className="font-medium text-slate-600">{st.order_location}</span>
                          ) : null}
                          {st.order_location && st.order_remarks ? <span className="mx-1.5 text-slate-300">·</span> : null}
                          {st.order_remarks ? <span className="italic">“{st.order_remarks}”</span> : null}
                        </div>
                      ) : null}
                    </li>
                  </TimelineRow>
                );
              })}
            </ul>
            {groupIdx < Object.entries(groups).length - 1 ? <div className="mx-5 border-t border-slate-100" /> : null}
          </div>
        ))}
      </div>
    </section>
  );
}

/* Rows slide in along the rail, top-down. */
function TimelineRow({ order = 0, children }) {
  const reduce = useReducedMotion();
  if (reduce) return children;
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: Math.min(order * 0.04, 0.6), ease: [0.22, 0.61, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function CopyHistoryButton({ items }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    const lines = items.map(s => {
      const t = s.whenISO ? formatInIST(s.whenISO) : "N/A";
      return `${t} | ${getOrderStatusName(String(s.order_status_code))} | ${s.order_location || "-"} | ${s.order_remarks || "-"}`;
    });
    navigator.clipboard?.writeText(lines.join("\n"));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={copy}
      className={`ml-2 inline-flex items-center gap-1.5 rounded border px-2.5 py-1 text-xs transition-all duration-200 ${
        copied
          ? "border-emerald-300 bg-emerald-50 text-emerald-700"
          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
      }`}
      title="Copy the timeline as text — handy for support tickets"
    >
      {copied ? <CheckIcon className="h-3.5 w-3.5" /> : <CopyIcon className="h-3.5 w-3.5" />}
      {copied ? "Copied" : "Copy history"}
    </button>
  );
}


/* -------- Footer rail (driven by history latest) -------- */
function FooterRail({ lastUpdatedISO, onBack, onRefetch, onCancel, latestStatusCode }) {
  const isPickupPending = PICKUP_PENDING_CODES.includes(latestStatusCode);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="text-xs text-slate-500">
        Last updated: <span className="font-semibold text-slate-700">{lastUpdatedISO ? formatInIST(lastUpdatedISO) : "N/A"}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
          ← Back to Shipments
        </button>
        {isPickupPending ? (
          <button
            onClick={onRefetch}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Refetch Label
          </button>
        ) : null}
        {isCancellable(latestStatusCode) ? (
          <button onClick={onCancel} className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm font-medium text-rose-600 hover:bg-rose-50">
            Cancel Shipment
          </button>
        ) : null}
      </div>
    </div>
  );
}
